import axios from "axios";
import i18next from "i18next";
import { cachedRead, readCache } from "../../../offline";
import { createResource } from "../../../offline/resource";

const base = `${process.env.REACT_APP_BACKEND_URL}/work-reports`;
const token = () => localStorage.getItem("token");
const headers = () => ({ headers: { Authorization: "Bearer " + token() } });

// Reports can be opened, created, edited and given photos without a connection (src/offline);
// signing, links, e-mail, PDF and invoicing need the server.
const reports = createResource({
  module: "work-reports",
  base,
  auth: headers,
  idPath: "data._id",
  versionPath: "data.updatedAt",
  unwrap: data => data.data,
  wrap: view => ({ data: view }),
  label: body => `${i18next.t("Work report", { ns: "dashboard" })} ${body.date || ""}`.trim(),
  // A report created offline, shown like a fresh draft until the server has numbered it
  blank: async (body, localId) => {
    const meta = (await readCache("work-reports:meta"))?.data;
    const settings = meta?.settings || {};
    const project = (meta?.projects || []).find(p => p._id === body.project);
    const clientId = body.client || project?.client || null;
    const client = (meta?.clients || []).find(c => c._id === clientId);
    return {
      _id: localId,
      number: i18next.t("New", { ns: "dashboard" }),
      status: "draft",
      date: body.date,
      project: body.project || null,
      client: clientId,
      siteAddress: "",
      orderReference: "",
      changeOrder: null,
      workDescription: "",
      internalNote: "",
      showPrices: settings.showPrices !== false,
      taxRate: settings.taxRate ?? 19,
      rounding: settings.rounding,
      labour: [],
      materials: [],
      photos: [],
      history: [],
      signLink: { active: false },
      signature: null,
      refusal: null,
      totals: {},
      projectInfo: project ? { _id: project._id, name: project.name } : null,
      clientInfo: client ? { _id: client._id, name: client.name, email: client.email || "" } : null,
      ...body
    };
  }
});

export const getMeta = () =>
  cachedRead("work-reports:meta", () => axios.get(`${base}/meta`, headers()));
export const saveSettings = body => axios.put(`${base}/settings`, body, headers());
export const listReports = filters =>
  reports.list(JSON.stringify(filters || {}), () => axios.post(`${base}/list`, filters, headers()));
export const getReport = id => reports.get(id);
export const createReport = body => reports.create(body);
export const updateReport = (id, body) => reports.update(id, body);
export const deleteReport = id =>
  reports.remove(id, serverId => axios.delete(`${base}/${serverId}`, headers()));
const online = (id, request) => reports.whenSynced(id, request);
export const proposeLabour = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/attendance`, body, headers()));
export const signReport = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/sign`, body, headers()));
export const refuseReport = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/refuse`, body, headers()));
export const reopenReport = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/reopen`, body, headers()));
export const createSignLink = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/sign-link`, body, headers()));
export const revokeSignLink = id =>
  online(id, serverId => axios.delete(`${base}/${serverId}/sign-link`, headers()));
export const emailReport = (id, body) =>
  online(id, serverId => axios.post(`${base}/${serverId}/email`, body, headers()));
export const invoiceReports = ids => axios.post(`${base}/invoice`, { ids }, headers());
export const deletePhoto = (id, photoId) =>
  reports.removePhoto(id, photoId, serverId =>
    axios.delete(`${base}/${serverId}/photos/${photoId}`, headers())
  );

// Without a connection the photo waits on the device (IndexedDB) and is uploaded later
export const uploadPhoto = (id, blob, { caption, width, height }) =>
  reports.addPhoto(
    id,
    blob,
    { ...(caption ? { caption } : {}), width: String(width || 0), height: String(height || 0) },
    blob.type === "image/png" ? "photo.png" : "photo.jpg"
  );

// Links that open in a new tab or an <img> cannot send a header: the token goes in the query.
export const pdfUrl = (id, lng) =>
  `${base}/${id}/pdf?token=${encodeURIComponent(token() || "")}&lng=${encodeURIComponent(lng || "de")}`;
export const photoUrl = (id, photoId) =>
  reports.photoUrl(
    photoId,
    () => `${base}/${id}/photos/${photoId}?token=${encodeURIComponent(token() || "")}`
  );

// The customer's signing page (no login; the tenant comes from ?domain=)
const publicBase = `${process.env.REACT_APP_BACKEND_URL}/public-work-reports`;
const domainQuery = domain => `?domain=${encodeURIComponent(domain || "")}`;
export const getPublicReport = (token, domain) =>
  axios.get(`${publicBase}/${token}${domainQuery(domain)}`);
export const signPublicReport = (token, domain, body) =>
  axios.post(`${publicBase}/${token}/sign${domainQuery(domain)}`, body);
export const publicPdfUrl = (token, domain) => `${publicBase}/${token}/pdf${domainQuery(domain)}`;
export const publicPhotoUrl = (token, domain, photoId) =>
  `${publicBase}/${token}/photos/${photoId}${domainQuery(domain)}`;
